import React, { useState } from 'react';
import { Box, Button, IconButton, styled } from '@mui/material';
import { ArrowBack, Menu as MenuIcon } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import NavigationMenu from './NavigationMenu';

const Logo = styled('img')({
  height: '32px',
  width: 'auto',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '@media (max-width: 600px)': {
    height: '28px',
  },
});

const TradePackagesHeader = ({ dark: _legacyDark = true, hideBack = false }) => {
  const { canGoBack, goBack, getBackDestination } = useNavigation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Shell redesign: the app is light-only — the legacy dark prop is ignored.
  const dark = false;
  const fg = dark ? '#FFFFFF' : '#1D1D1F';
  const fgMuted = dark ? 'rgba(255,255,255,0.65)' : '#6E6E73';
  const hoverBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0, 0, 0, 0.04)';
  const headerBg = dark ? 'rgba(0,0,0,0.6)' : 'rgba(255, 255, 255, 0.8)';
  const headerBorder = dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0, 0, 0, 0.06)';
  const logoSrc = dark ? '/logo-white.svg' : '/logo.svg';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2000,
          height: { xs: '56px', sm: '64px' },
          padding: { xs: '0 16px', sm: '0 32px' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: headerBg,
          backdropFilter: 'blur(20px)',
          borderBottom: headerBorder,
        }}
      >
        {/* Left section - Back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!hideBack && canGoBack() && (
            <Box
              component={Link}
              to={getBackDestination()}
              onClick={(e) => {
                if (!e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  goBack();
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: fgMuted,
                textDecoration: 'none',
                fontSize: { xs: '15px', sm: '15px' },
                fontWeight: 400,
                letterSpacing: '-0.01em',
                padding: { xs: '14px 24px', sm: '12px 24px' },
                minHeight: '44px',
                borderRadius: '980px',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  color: fg,
                  background: hoverBg,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              <ArrowBack sx={{ fontSize: '20px' }} />
              Back
            </Box>
          )}
        </Box>

        {/* Center section - Taco logo */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Logo
            src={logoSrc}
            alt="Fantasy Taco"
            onClick={() => navigate('/')}
          />
        </Box>

        {/* Right section - Menu icon */}
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton
            onClick={() => setMenuOpen(true)}
            sx={{
              color: fg,
              width: '44px',
              height: '44px',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              '&:hover': {
                background: hoverBg,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Navigation Menu Modal */}
      <NavigationMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export default TradePackagesHeader;
